import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Input, Spinner } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import cs_analysis_fetch from '../../utils/cs_analysis_fetch';
import Thread, { MediaGallery } from '../StoreDashboard/ClientHealthTab/CommsThread';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { CreateTicketModal, WaMembers } from '../StoreDashboard/ClientHealthTab/ClientComms';
import GroupMembersDrawer from '../StoreDashboard/ClientHealthTab/GroupMembersDrawer';
import NotifyToggle from '../StoreDashboard/ClientHealthTab/NotifyToggle';
import {
    GROUPS_TAB, PRIVATE_TAB, chat_row_org, chat_row_title, clear_needs_human, feed_query, in_scope, inbox_tab_badges,
    hits_in_tab, inbox_tab_of, is_user_chat, matches_type_filter, merge_tab_feeds, needs_human_since, owner_verified,
    private_chat_sections, private_section_of, search_query,
} from './user_chat';
import { AttendedMark, KIND_CHIP, KindIcon, VerifiedTick, WaitBadge } from './row_bits';

/**
 * Cross-org Chat inbox (cs-analysis GET /comms/feed): every client
 * conversation, in two tabs (Nikhil 2026-09-24):
 *   Groups         group channels and ticket threads, unread and latest
 *                  activity first, with the Unread / My chats / Recent pills;
 *   Private chats  each client user's private portal "Chat" (kind USER_CHAT:
 *                  the Infurnia Assistant answers first, reps take part in the
 *                  same thread, "Talk to a person" sets needsHumanAt), in three
 *                  sections — Needs a person (oldest ask first), Answered
 *                  (latest ask first), Handled by the assistant (collapsed).
 * Each tab has its own feed request (?kinds=…; private chats get a wider
 * window) and the rows are re-split by kind, so an api without ?kinds still
 * lands every row in the right tab. The per-org Client Comms tab stays the
 * deep-work surface; this is the daily driver that replaces monitoring
 * WhatsApp Web.
 *
 * ONE Thread mounts at a time (the open conversation) — rendering a Thread
 * advances the read cursor, so list previews must never mount threads.
 */

const FEED_PATH = '/api/v1/comms/feed';

const fmt_ts = (d) => {
    try {
        const t = new Date(d); const now = new Date();
        const same_day = t.toDateString() === now.toDateString();
        return same_day
            ? t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
            : t.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch (e) { return ''; }
};
const title_case = (s) => (s || '').split(/[_\s-]+/).map((w) => (w ? w[0].toUpperCase() + w.slice(1) : '')).join(' ');
// Search hits: "Chat · <person>" for user chats, ticket title, else the group.
const search_kind_label = (r) => (is_user_chat(r) ? `Chat${chat_row_title(r) ? ` · ${chat_row_title(r)}` : ''}` : r.kind === 'TICKET' ? r.title : title_case(r.groupKey || 'general'));

// Group/Ticket filters dropped (Nikhil 2026-07-14): ticket work lives in the
// Client Tickets workspace now, and "group chats" was ~the whole list. The
// People pill (2026-09-22) became the Private chats tab (2026-09-24).
const TYPE_FILTERS = [['unread', 'Unread only'], ['mine', 'My chats'], ['recent', 'Recent (7d)']];

const TAB_BADGE = { marginLeft: 6, fontSize: 10, fontWeight: 600, borderRadius: 9, padding: '0 6px', lineHeight: '16px', display: 'inline-block', color: '#fff', background: '#2f6fed', border: '1px solid #2f6fed' };
const WAIT_TAB_BADGE = { ...TAB_BADGE, color: '#8a6d1d', background: '#fff8e6', border: '1px solid #e6b35a' };
const SECTION_HEAD = { padding: '5px 12px', fontSize: 11, fontWeight: 600, color: '#7f8c8d', background: '#fafbfc', borderBottom: '1px solid #eef1f3', display: 'flex', alignItems: 'center', gap: 6 };

export default function Chat() {
    const [conversations, set_conversations] = useState(null);
    const [access, set_access] = useState(null); // {level: 'rep'|'lead'|'admin', hasTeam}
    const [selected_id, set_selected_id] = useState(null);
    const [scope, set_scope] = useState('my'); // my | team | all — pills gated by access, apply to both tabs
    const [tab, set_tab] = useState(GROUPS_TAB); // groups | private
    const [filter, set_filter] = useState(null); // Groups tab pill: null = everything; else unread|mine|recent
    const [show_assistant, set_show_assistant] = useState(null); // "Handled by the assistant": null = auto (open only when nothing else is listed)
    const [q, set_q] = useState('');
    const [search_results, set_search_results] = useState(null); // message hits when q >= 3 chars
    const [search_channels, set_search_channels] = useState([]); // title/org/people hits
    const [members_for, set_members_for] = useState(null); // conversation for the ⋯ members modal
    const [error, set_error] = useState(null);

    // Both tabs load on every poll (the tab badges need both): groups and tickets in the api's default
    // window, private chats in a wider one so busy groups never push older ones out. The pills are local.
    const load = useCallback(async () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        try {
            const [groups, privates] = await Promise.all([
                cs_analysis_fetch({ path: FEED_PATH, query: feed_query(GROUPS_TAB) }),
                cs_analysis_fetch({ path: FEED_PATH, query: feed_query(PRIVATE_TAB) }),
            ]);
            set_conversations(merge_tab_feeds(groups && groups.conversations, privates && privates.conversations));
            set_access((groups && groups.access) || (privates && privates.access) || { level: 'rep', hasTeam: false });
            set_error(null);
        } catch (e) {
            set_error((e && e.message) || 'comms unavailable');
            set_conversations((cur) => cur || []);
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

    // Scope pills per access rights (backend row-gates anyway — this is UX):
    // My for everyone; Team only for leads/admins with a team; All admins only.
    const scopes = ['my'].concat(access && access.hasTeam ? ['team'] : []).concat(access && access.level === 'admin' ? ['all'] : []);
    const effective_scope = scopes.includes(scope) ? scope : 'my';

    // Message search (server-side, access-gated) — debounced, >= 3 chars, kept to the open tab (?kinds, and the hits
    // re-split by kind for an api that ignores it) and to the pill (?scope); switching tab or pill searches again.
    useEffect(() => {
        const needle = q.trim();
        if (needle.length < 3) { set_search_results(null); set_search_channels([]); return undefined; }
        // stale guard: a slower response for an older needle must never
        // clobber the results of what's currently typed.
        let stale = false;
        const t = setTimeout(async () => {
            try {
                const r = await cs_analysis_fetch({ path: '/api/v1/comms/search', query: search_query(tab, needle, effective_scope) });
                if (stale) return;
                set_search_results(hits_in_tab(r && r.results, tab));
                set_search_channels(hits_in_tab(r && r.channels, tab));
            } catch (e) { if (!stale) { set_search_results([]); set_search_channels([]); } }
        }, 400);
        return () => { stale = true; clearTimeout(t); };
    }, [q, tab, effective_scope]);


    const needle = q.trim().toLowerCase();
    // The scope applies to both tabs and to their badges; the search box and the pills to the list only.
    const scoped = (conversations || []).filter((c) => in_scope(c, effective_scope));
    const badges = inbox_tab_badges(scoped);
    const tab_rows = scoped.filter((c) => inbox_tab_of(c) === tab
        && (!needle || `${c.clientName} ${c.title} ${c.groupKey || ''} ${chat_row_title(c)}`.toLowerCase().includes(needle)));
    const group_list = tab === GROUPS_TAB ? tab_rows.filter((c) => matches_type_filter(c, filter)) : [];
    const sections = tab === PRIVATE_TAB ? private_chat_sections(tab_rows) : null;
    // The assistant-only chats are the quiet majority: collapsed until the rep opens them, except while
    // a search narrows the list or nothing else is listed. The rep's own Show / Hide wins.
    const assistant_open = !!sections && (show_assistant !== null ? show_assistant
        : !!needle || sections.waiting.length + sections.answered.length === 0);
    const assistant_unread = sections ? sections.assistant.filter((c) => c.unread > 0).length : 0;
    const [external_selected, set_external_selected] = useState(null); // search hit not in the feed
    const [focus_seq, set_focus_seq] = useState(null); // jump-to-message target
    const [me_id, set_me_id] = useState(null);
    const [me_name, set_me_name] = useState(null); // names the optimistic "Attended by" after a reply
    const [ticket_prefill, set_ticket_prefill] = useState(null);
    const [show_ticket_modal, set_show_ticket_modal] = useState(false);
    const [show_media, set_show_media] = useState(false);
    const [show_members, set_show_members] = useState(false); // group Members drawer (CLIENT_GROUP only)
    const close_members = useCallback(() => set_show_members(false), []);
    useEffect(() => {
        cs_analysis_fetch({ path: '/api/v1/auth/me' })
            .then((r) => {
                const u = (r && r.user) || {};
                set_me_id(u.id);
                set_me_name([u.firstName, u.lastName].filter(Boolean).join(' ') || null);
            })
            .catch(() => {});
    }, []);

    // Pre-warm AI reply suggestions while the Chat page is open, ONLY for
    // conversations in the rep's My list (Nikhil 2026-07-14). The server
    // caches by (channel, last message), so opening a warmed thread shows
    // the ghost instantly; at most 3 warms per feed poll.
    const warmed = useRef(new Set());
    useEffect(() => {
        const now = Date.now();
        const cands = (conversations || [])
            .filter((c) => c.scope === 'my'
                && c.unread > 0
                && (c.lastAuthorType === 'GUEST' || c.lastAuthorType === 'CLIENT_USER')
                && c.lastMessageAt && now - new Date(c.lastMessageAt).getTime() < 48 * 3600 * 1000)
            .slice(0, 8)
            .filter((c) => !warmed.current.has(`${c.channelId}:${c.lastMessageAt}`))
            .slice(0, 3);
        for (const c of cands) {
            warmed.current.add(`${c.channelId}:${c.lastMessageAt}`);
            cs_analysis_fetch({ path: `/api/v1/comms/channels/${c.channelId}/suggest-reply`, method: 'POST' }).catch(() => {});
        }
    }, [conversations]);
    const selected = (conversations || []).find((c) => c.channelId === selected_id) ||
        (external_selected && external_selected.channelId === selected_id ? external_selected : null);

    const open = (c, seq) => {
        set_selected_id(c.channelId);
        set_focus_seq(seq || null);
        set_show_media(false);
        set_show_members(false);
        // Always pin: the open conversation must survive dropping off the
        // 100-row feed window (draft/thread would blank otherwise).
        set_external_selected(c);
        // Optimistic: the mounted Thread advances the server cursor.
        set_conversations((cur) => (cur || []).map((x) => (x.channelId === c.channelId ? { ...x, unread: 0 } : x)));
    };

    // Deep link — /home/chat?channel=<id>&seq=<n> — the target of a web-push
    // click (public/push-sw.js → Admin layout → history.push) and of the
    // Google Chat DMs. Select the conversation from the feed, or fetch its
    // summary when it sits outside the loaded feed window, switch to the tab
    // of its kind, and jump to the message. Re-applied whenever the query
    // string changes; the ref stops the same link re-opening on every poll.
    const location = useLocation();
    const applied_link = useRef(null);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const channel = params.get('channel');
        if (!channel) return;
        const seq_raw = params.get('seq');
        const seq = seq_raw && Number.isFinite(Number(seq_raw)) ? Number(seq_raw) : null;
        const key = `${channel}:${seq || ''}`;
        if (applied_link.current === key) return;
        if (conversations === null) return; // feed not loaded yet; runs again when it is
        const in_feed = conversations.find((c) => c.channelId === channel);
        applied_link.current = key;
        if (in_feed) {
            // Show the row in the left list too: admins/leads land on "My",
            // which may not include this conversation. Feed scopes are
            // my | team | other; the pills are my | team | all. `effective_scope`
            // clamps to what access allows once /feed's access has loaded (it
            // can land a render after `conversations`), so set it unguarded.
            if (in_feed.scope === 'team') set_scope('team');
            else if (in_feed.scope && in_feed.scope !== 'my') set_scope('all');
            set_tab(inbox_tab_of(in_feed));
            if (private_section_of(in_feed) === 'assistant') set_show_assistant(true); // the row is listed, not collapsed
            open(in_feed, seq);
            return;
        }
        cs_analysis_fetch({ path: `/api/v1/comms/channels/${channel}/summary` })
            .then((r) => {
                if (!(r && r.conversation)) return;
                set_tab(inbox_tab_of(r.conversation));
                open(r.conversation, seq);
            })
            .catch(() => {});
    }, [location.search, conversations]); // eslint-disable-line react-hooks/exhaustive-deps

    // One conversation row — the same in both tabs and every section.
    const render_row = (c) => (
        <div key={c.channelId} onClick={() => open(c)} data-testid='chat-row' data-channel-id={c.channelId}
            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f3f5',
                // A chat waiting for a person is highlighted like an unread one, only warmer.
                background: selected_id === c.channelId ? '#eef4ff' : is_user_chat(c) && c.needsHumanAt ? '#fffaf0' : c.unread ? '#fbfdff' : '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <b style={{ fontSize: 13, color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
                    <KindIcon c={c} />{chat_row_title(c)}{owner_verified(c) ? <VerifiedTick /> : null}
                    {/* Person first, then their org — so a private row lines up with the group rows' org titles. */}
                    {is_user_chat(c) && chat_row_org(c) ? <span style={{ fontWeight: 400, color: '#7f8c8d' }}> · {chat_row_org(c)}</span> : null}
                </b>
                <span style={{ fontSize: 11, color: '#95a5a6', whiteSpace: 'nowrap' }}>
                    {fmt_ts(c.lastMessageAt)}
                    {/* WhatsApp members/mapping is an org-group affair — not for a person's portal chat. */}
                    {is_user_chat(c) ? null : (
                        <span title='Members & mapping' onClick={(e) => { e.stopPropagation(); set_members_for(c); }}
                            style={{ cursor: 'pointer', marginLeft: 8, color: '#8a97a0', fontSize: 14, fontWeight: 700 }}>⋯</span>
                    )}
                </span>
            </div>
            <div style={{ fontSize: 11, color: '#7f8c8d', margin: '1px 0', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                {is_user_chat(c) ? (
                    <>
                        <span style={KIND_CHIP}>Chat</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>private · assistant + CS</span>
                        {c.needsHumanAt ? <span style={{ marginLeft: 'auto' }}><WaitBadge at={c.needsHumanAt} /></span>
                            : <span style={{ marginLeft: 'auto' }}><AttendedMark c={c} /></span>}
                    </>
                ) : (
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.kind === 'TICKET' ? 'Ticket' : title_case(c.groupKey || 'general')}
                        {c.bridged ? ' ⇄' : ''}
                        {c.kind === 'TICKET' ? ` · ${c.title}` : ''}
                    </span>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#5d6d7e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }}>
                    {c.lastAuthorName ? `${c.lastAuthorName}: ` : ''}{c.lastBody || ''}
                </span>
                {c.unread ? (
                    c.participating ? (
                        <Badge color='primary' pill style={{ fontSize: 10 }}>{c.unread > 99 ? '99+' : c.unread}</Badge>
                    ) : (
                        // Never-opened channel: the cursor fallback counts ALL
                        // history as unread — show a quiet "new" chip instead.
                        <span style={{ fontSize: 9.5, color: '#2f6fed', border: '1px solid #cfe0ff', borderRadius: 9, padding: '1px 7px', background: '#f4f8ff' }}>new</span>
                    )
                ) : null}
            </div>
        </div>
    );

    return (
        <div style={{ padding: '24px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, rowGap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0 }}>Chat</h4>
                {/* Whose conversations: in the header line, not a line of its own over the list (Nikhil 2026-09-25).
                    Chips, not a .btn-group — a btn-group is one rigid box that can't wrap. */}
                {scopes.length > 1 ? (
                    <div role='group' aria-label='Whose conversations' data-testid='chat-scope-pills' style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {scopes.map((sc) => (
                            <button key={sc} type='button' onClick={() => set_scope(sc)} aria-pressed={effective_scope === sc} data-testid={`chat-scope-${sc}`}
                                style={{
                                    border: '1px solid ' + (effective_scope === sc ? '#2c3e50' : '#d5dbe1'),
                                    background: effective_scope === sc ? '#2c3e50' : '#fff',
                                    color: effective_scope === sc ? '#fff' : '#4a5866',
                                    borderRadius: 12, padding: '1px 10px', fontSize: 11.5, cursor: 'pointer', lineHeight: '18px',
                                }}>
                                {sc === 'my' ? 'My' : title_case(sc)}
                            </button>
                        ))}
                    </div>
                ) : null}
                <span style={{ fontSize: 12, color: '#7f8c8d' }}>all client conversations, latest first</span>
                {error ? <span style={{ fontSize: 12, color: '#c0392b' }}>{error}</span> : null}
            </div>
            <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 160px)', minHeight: 480 }}>
                <div style={{ width: 360, minWidth: 300, display: 'flex', flexDirection: 'column' }}>
                    {/* Groups | Private chats (Nikhil 2026-09-24): a person's thread never mixes with the 400+ WhatsApp groups. */}
                    <div role='tablist' style={{ display: 'flex', borderBottom: '1px solid #e3e8ec', marginBottom: 8 }}>
                        {[[GROUPS_TAB, 'Groups', badges.groups], [PRIVATE_TAB, 'Private chats', badges.private]].map(([t, label, badge]) => (
                            <button key={t} type='button' role='tab' aria-selected={tab === t} data-testid={`chat-tab-${t}`}
                                onClick={() => set_tab(t)}
                                title={t === GROUPS_TAB ? 'WhatsApp groups, org channels and tickets' : 'Each client user\'s private chat with the assistant and the Infurnia team'}
                                style={{
                                    flex: 1, background: 'none', border: 'none', borderBottom: '2px solid ' + (tab === t ? '#2f6fed' : 'transparent'),
                                    marginBottom: -1, padding: '5px 4px 6px', fontSize: 13, cursor: 'pointer',
                                    fontWeight: tab === t ? 600 : 400, color: tab === t ? '#2c3e50' : '#6b7a86',
                                }}>
                                {label}
                                {badge ? (
                                    <span style={badge.waiting ? WAIT_TAB_BADGE : TAB_BADGE}
                                        title={badge.waiting ? `${badge.count} waiting for a person` : `${badge.count} with unread messages`}>
                                        {badge.waiting ? <><span role='img' aria-label='needs a person'>🙋</span> </> : null}{badge.count}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                    </div>
                    {tab === GROUPS_TAB ? (
                        <div style={{ display: 'flex', gap: 4, rowGap: 5, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            {TYPE_FILTERS.map(([f, label]) => (
                                <button key={f} type='button' title={label}
                                    onClick={() => set_filter(filter === f ? null : f)}
                                    style={{
                                        border: '1px solid ' + (filter === f ? '#2f6fed' : '#d5dbe1'),
                                        background: filter === f ? '#2f6fed' : '#fff',
                                        color: filter === f ? '#fff' : '#4a5866',
                                        borderRadius: 12, padding: '1px 10px', fontSize: 11.5, cursor: 'pointer', lineHeight: '18px',
                                    }}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <div style={{ position: 'relative', marginBottom: 8 }}>
                        <Input placeholder={tab === PRIVATE_TAB ? 'Search org / person…' : 'Search org / group…'} value={q} onChange={(e) => set_q(e.target.value)}
                            style={{ fontSize: 13, paddingRight: 28 }} />
                        {q ? (
                            <span onClick={() => set_q('')} title='Clear search'
                                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#95a5a6', fontSize: 14, lineHeight: 1 }}>✕</span>
                        ) : null}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e8ecef', borderRadius: 8, background: '#fff' }}>
                        {search_results != null && search_channels.length ? (
                            <div style={{ borderBottom: '2px solid #e8ecef' }}>
                                <div style={{ padding: '6px 12px', fontSize: 11, color: '#7f8c8d', background: '#fafbfc' }}>Chats matching “{q.trim()}”</div>
                                {search_channels.slice(0, 8).map((r) => (
                                    <div key={r.channelId}
                                        onClick={() => open({ channelId: r.channelId, clientName: r.clientName, storeId: r.storeId, kind: r.kind, groupKey: r.groupKey, title: r.title, bridged: false, unread: 0, chatOwner: r.chatOwner, needsHumanAt: r.needsHumanAt })}
                                        style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f6f8f9' }}>
                                        <b style={{ fontSize: 12, color: '#2c3e50' }}>{r.clientName}</b>
                                        <span style={{ fontSize: 11, color: '#7f8c8d' }}> · {search_kind_label(r)} · matched {r.matchedOn}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {search_results != null ? (
                            <div style={{ borderBottom: '2px solid #e8ecef' }}>
                                <div style={{ padding: '6px 12px', fontSize: 11, color: '#7f8c8d', background: '#fafbfc' }}>
                                    Messages matching “{q.trim()}” {search_results.length ? `(${search_results.length})` : ''}
                                </div>
                                {search_results.length === 0 ? (
                                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#95a5a6' }}>No message hits.</div>
                                ) : null}
                                {search_results.slice(0, 20).map((r) => (
                                    <div key={r.messageId}
                                        onClick={() => open({ channelId: r.channelId, clientName: r.clientName, storeId: r.storeId, kind: r.kind, groupKey: r.groupKey, title: r.title, bridged: false, unread: 0, chatOwner: r.chatOwner, needsHumanAt: r.needsHumanAt }, r.seq)}
                                        style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #f6f8f9' }}>
                                        <div style={{ fontSize: 11, color: '#7f8c8d' }}>
                                            <b style={{ color: '#2c3e50' }}>{r.clientName}</b> · {search_kind_label(r)} · {fmt_ts(r.createdAt)}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#5d6d7e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.authorName ? `${r.authorName}: ` : ''}{r.body}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {conversations == null ? <div style={{ padding: 16 }}><Spinner size='sm' /> loading…</div> : null}
                        {conversations != null && tab === GROUPS_TAB ? (
                            group_list.length ? group_list.map(render_row)
                                : <div style={{ padding: 16, fontSize: 12, color: '#95a5a6' }}>No conversations.</div>
                        ) : null}
                        {conversations != null && sections ? (
                            tab_rows.length === 0 ? (
                                <div style={{ padding: 16, fontSize: 12, color: '#95a5a6' }}>No private chats.</div>
                            ) : (
                                <>
                                    {sections.waiting.length ? (
                                        <div data-testid='chat-section-waiting'>
                                            <div style={{ ...SECTION_HEAD, color: '#8a6d1d', background: '#fff8e6' }}>
                                                <span role='img' aria-label='needs a person'>🙋</span> Needs a person · {sections.waiting.length}
                                            </div>
                                            {sections.waiting.map(render_row)}
                                        </div>
                                    ) : null}
                                    {sections.answered.length ? (
                                        <div data-testid='chat-section-answered'>
                                            <div style={SECTION_HEAD}>Answered · {sections.answered.length}</div>
                                            {sections.answered.map(render_row)}
                                        </div>
                                    ) : null}
                                    {sections.assistant.length ? (
                                        <div data-testid='chat-section-assistant'>
                                            <button type='button' aria-expanded={assistant_open} onClick={() => set_show_assistant(!assistant_open)}
                                                title='Private chats that never asked for a person'
                                                style={{ ...SECTION_HEAD, width: '100%', border: 'none', borderBottom: '1px solid #eef1f3', cursor: 'pointer', textAlign: 'left' }}>
                                                Handled by the assistant · {sections.assistant.length}
                                                {assistant_unread ? <span style={{ fontWeight: 400, color: '#2f6fed' }}>· {assistant_unread} unread</span> : null}
                                                <span style={{ marginLeft: 'auto', fontWeight: 400, color: '#2f6fed' }}>
                                                    {assistant_open ? 'Hide' : `Show ${sections.assistant.length}`}
                                                </span>
                                            </button>
                                            {assistant_open ? sections.assistant.map(render_row) : null}
                                        </div>
                                    ) : null}
                                </>
                            )
                        ) : null}
                    </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e8ecef', borderRadius: 8, background: '#fff', padding: 12 }}>
                    {!selected ? (
                        <div style={{ margin: 'auto', color: '#95a5a6', fontSize: 13 }}>
                            Select a conversation to read and reply.
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, borderBottom: '1px solid #f1f3f5', paddingBottom: 8 }}>
                                <b style={{ fontSize: 14 }}><KindIcon c={selected} size={13} />{chat_row_title(selected)}{owner_verified(selected) ? <VerifiedTick size={12} /> : null}</b>
                                <span style={{ fontSize: 12, color: '#7f8c8d' }}>
                                    {is_user_chat(selected)
                                        ? <><span style={KIND_CHIP}>Chat</span> · {selected.clientName}</>
                                        : selected.kind === 'TICKET' ? `Ticket · ${selected.title}` : title_case(selected.groupKey || 'general')}
                                    {selected.bridged ? ' · ⇄ WhatsApp' : ''}
                                </span>
                                {is_user_chat(selected) && selected.needsHumanAt ? (
                                    // Cleared by the backend when a rep posts (and optimistically via on_sent below).
                                    <span title='The client asked to talk to a person — your reply clears this'
                                        style={{ fontSize: 11, color: '#8a6d1d', background: '#fff8e6', border: '1px solid #f0d48a', borderRadius: 10, padding: '1px 9px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        <span role='img' aria-label='needs a person'>🙋</span> Needs a person since {needs_human_since(selected.needsHumanAt)}
                                    </span>
                                ) : null}
                                <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'baseline' }}>
                                    {selected.kind === 'CLIENT_GROUP' || is_user_chat(selected) ? (
                                        // Their group + explicits (Nikhil 2026-09-13): reading a channel no
                                        // longer subscribes the rep; the row's notify says owner / following /
                                        // muted. Patch our copy of the row so a later feed poll keeps the click.
                                        <NotifyToggle channel_id={selected.channelId} notify={selected.notify}
                                            on_change={(notify) => {
                                                set_conversations((cur) => (cur ? cur.map((x) => (x.channelId === selected.channelId ? { ...x, notify } : x)) : cur));
                                                set_external_selected((x) => (x && x.channelId === selected.channelId ? { ...x, notify } : x));
                                            }} />
                                    ) : null}
                                    {selected.kind === 'CLIENT_GROUP' ? (
                                        // Who can see this group: portal users (add / remove), WhatsApp members, Infurnia team.
                                        <span onClick={() => set_show_members(true)} title='Members of this group — portal users with access, WhatsApp members, Infurnia team'
                                            style={{ fontSize: 12, color: '#2f6fed', cursor: 'pointer' }}>
                                            <span role='img' aria-label='members'>👥</span> members
                                        </span>
                                    ) : null}
                                    <span onClick={() => set_show_media(true)} title='Photos, videos & files in this conversation'
                                        style={{ fontSize: 12, color: '#2f6fed', cursor: 'pointer' }}>
                                        🖼 media
                                    </span>
                                    <span onClick={() => { set_ticket_prefill(null); set_show_ticket_modal(true); }}
                                        style={{ fontSize: 12, color: '#2f6fed', cursor: 'pointer', fontWeight: 600 }}>
                                        + New ticket
                                    </span>
                                    <Link to={`/home/store_dashboard/${selected.storeId}/comms`} style={{ fontSize: 12 }} title='Bridge & member management'>
                                        org dashboard →
                                    </Link>
                                    <span onClick={() => { set_selected_id(null); set_external_selected(null); set_focus_seq(null); }} title='Close conversation'
                                        style={{ fontSize: 15, color: '#95a5a6', cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>✕</span>
                                </span>
                            </div>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <Thread key={`${selected.channelId}:${focus_seq || ''}`} channel_id={selected.channelId}
                                    allow_internal={selected.kind === 'TICKET'} height='100%'
                                    placeholder={`Message ${chat_row_title(selected)}…`}
                                    focus_seq={focus_seq} store_id={selected.storeId} me_id={me_id}
                                    on_sent={() => {
                                        // A representative's post clears needsHumanAt and marks the request attended
                                        // server-side; reflect it at once (the feed poll confirms within 30s) on both
                                        // the feed row and the pin — the row moves to "Answered".
                                        if (!is_user_chat(selected) || !selected.needsHumanAt) return;
                                        set_conversations((cur) => clear_needs_human(cur, selected.channelId, me_name));
                                        set_external_selected((x) => (x && x.channelId === selected.channelId ? clear_needs_human([x], x.channelId, me_name)[0] : x));
                                    }}
                                    on_to_ticket={(m) => {
                                        // Word-boundary cut — a mid-word slice made ugly default titles.
                                        const raw = m.body || 'attachment';
                                        const cut = raw.length > 72 ? raw.slice(0, 72).replace(/\s+\S*$/, '') + '…' : raw;
                                        set_ticket_prefill({ title: cut, description: `From ${m.authorName || 'chat'} (${selected.clientName}):\n${raw}` });
                                        set_show_ticket_modal(true);
                                    }} />
                            </div>
                        </>
                    )}
                </div>
            </div>
            {selected ? (
                <MediaGallery channel_id={selected.channelId} open={show_media}
                    on_close={() => set_show_media(false)}
                    on_jump={(seq) => set_focus_seq(seq)} />
            ) : null}
            {selected && show_members && selected.kind === 'CLIENT_GROUP' ? (
                <GroupMembersDrawer channel_id={selected.channelId} store_id={selected.storeId} conversation={selected}
                    on_close={close_members} />
            ) : null}
            <Modal isOpen={!!members_for} toggle={() => set_members_for(null)} size='lg'>
                <ModalHeader toggle={() => set_members_for(null)}>
                    {members_for ? `${members_for.clientName} — WhatsApp members & mapping` : ''}
                </ModalHeader>
                <ModalBody>
                    {members_for ? <WaMembers store_id={members_for.storeId} default_open /> : null}
                </ModalBody>
            </Modal>
            {selected ? (
                <CreateTicketModal store_id={selected.storeId} open={show_ticket_modal}
                    on_close={() => set_show_ticket_modal(false)}
                    on_created={() => { set_show_ticket_modal(false); load(); }}
                    upload_channel_id={selected.kind === 'CLIENT_GROUP' ? selected.channelId : null}
                    initial={ticket_prefill} />
            ) : null}
        </div>
    );
}
